import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import BoardWorkspace from "@/components/BoardWorkspace";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const categories = ["vehicle", "equipment", "trailer", "tool", "material"];
const emptyAsset = {
  name: "",
  category: "vehicle",
  quantity: 1,
  condition: "good",
  make_model: "",
  serial_number: "",
  location: "",
  notes: "",
};

export default function AssetsPage() {
  const { canManageAssets } = useAuth();
  const [assets, setAssets] = useState([]);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyAsset);
  const [editingId, setEditingId] = useState("");
  const load = useCallback(
    async () =>
      setAssets(
        (await axios.get(`${API}/assets`, { params: { category, search } }))
          .data.assets,
      ),
    [category, search],
  );
  useEffect(() => {
    if (canManageAssets)
      load().catch(() => toast.error("Assets could not be loaded."));
  }, [canManageAssets, load]);
  if (!canManageAssets) return <Navigate replace to="/master-project-board" />;
  const save = async (event) => {
    event.preventDefault();
    try {
      if (editingId) await axios.patch(`${API}/assets/${editingId}`, form);
      else await axios.post(`${API}/assets`, form);
      setForm(emptyAsset);
      setEditingId("");
      await load();
      toast.success("Asset saved.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Asset could not be saved.");
    }
  };
  const edit = (asset) => {
    setEditingId(asset.id);
    setForm({ ...asset });
  };
  const remove = async (asset) => {
    try {
      await axios.delete(`${API}/assets/${asset.id}`);
      await load();
      toast.success("Asset removed.");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Asset could not be removed.",
      );
    }
  };
  return (
    <BoardWorkspace
      eyebrow="Owner reference catalog"
      headingTestId="assets-heading"
      subtitle="A simple owner-only list for vehicles, equipment, trailers, tools, and materials. No checkout, assignment, maintenance, or alert workflow is included."
      title="Inventory & Assets"
    >
      <section className="project-notes-panel" data-testid="assets-workspace">
        <div className="communication-filters">
          <select
            data-testid="asset-category-filter"
            onChange={(e) => setCategory(e.target.value)}
            value={category}
          >
            <option value="">All categories</option>
            {categories.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <input
            data-testid="asset-search-input"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, serial, or location"
            value={search}
          />
        </div>
        <form
          className="field-note-form"
          data-testid="asset-form"
          onSubmit={save}
        >
          <input
            data-testid="asset-name-input"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name"
            required
            value={form.name}
          />
          <select
            data-testid="asset-category-select"
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            value={form.category}
          >
            {categories.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <input
            data-testid="asset-quantity-input"
            min="0"
            onChange={(e) =>
              setForm({ ...form, quantity: Number(e.target.value) })
            }
            placeholder="Quantity"
            required
            type="number"
            value={form.quantity}
          />
          <input
            data-testid="asset-condition-input"
            onChange={(e) => setForm({ ...form, condition: e.target.value })}
            placeholder="Condition"
            required
            value={form.condition}
          />
          <input
            data-testid="asset-model-input"
            onChange={(e) => setForm({ ...form, make_model: e.target.value })}
            placeholder="Make / model"
            value={form.make_model}
          />
          <input
            data-testid="asset-serial-input"
            onChange={(e) =>
              setForm({ ...form, serial_number: e.target.value })
            }
            placeholder="Serial number"
            value={form.serial_number}
          />
          <input
            data-testid="asset-location-input"
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="Location"
            value={form.location}
          />
          <textarea
            data-testid="asset-notes-input"
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Notes"
            value={form.notes}
          />
          <button
            className="invoice-primary-button"
            data-testid="asset-save-button"
            type="submit"
          >
            {editingId ? "Save asset" : "Add asset"}
          </button>
          {editingId && (
            <button
              data-testid="asset-cancel-button"
              onClick={() => {
                setEditingId("");
                setForm(emptyAsset);
              }}
              type="button"
            >
              Cancel
            </button>
          )}
        </form>
        <div className="workspace-grid" data-testid="assets-list">
          {assets.map((asset) => (
            <article
              className="project-note-entry"
              data-testid={`asset-${asset.id}`}
              key={asset.id}
            >
              <strong>
                {asset.name} · {asset.category}
              </strong>
              <p>
                Quantity: {asset.quantity} · Condition: {asset.condition} ·
                Location: {asset.location || "not set"}
              </p>
              <small>
                {asset.make_model || "No model"} ·{" "}
                {asset.serial_number || "No serial number"}
              </small>
              {asset.notes && <p>{asset.notes}</p>}
              <div className="communication-filters">
                <button
                  data-testid={`asset-edit-${asset.id}`}
                  onClick={() => edit(asset)}
                  type="button"
                >
                  Edit
                </button>
                <button
                  data-testid={`asset-delete-${asset.id}`}
                  onClick={() => remove(asset)}
                  type="button"
                >
                  Remove
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
      <Toaster richColors position="bottom-right" />
    </BoardWorkspace>
  );
}
